import type {Recipient, SuggestionType} from 'src/types/contacts';
import type {
  TwilioPhoneType,
  TypeaheadExactMatch,
  TypeaheadResults,
} from 'src/types/draft-messages';
import {
  getVirtualContact,
  getVirtualContactPhoneNumber,
} from 'src/utils/contacts';
import type {BroadcastSuppressionPhoneDetails} from '../broadcast-suppression/types';
import {getFullName} from 'src/utils';
import {normalizePhone} from 'src/utils/phone';
import {camel} from 'src/utils/camel-snake';
import type {AtsContact} from 'src/chat-extension/messages';
import type {TranslationFunction} from 'src/types/language-support';


const LIMIT = 5;

export const removeZeroForNationalPhoneNumbers = (number: string) => {
  if (number.length > 10 && number.startsWith('0')) {
    return number.slice(1);
  }
  return number;
};

export const checkIfRecipientAlreadyExists = (
  recipients: Recipient[],
  recipient: Recipient,
  currentPhoneCountry: string,
): boolean => {
  // Handle thread list suggestions - compare by ID
  if (checkIfRecipientIsThreadListSuggestion(recipient)) {
    return false;
  }

  // For other recipients, compare by phone number
  const phoneNumber = getVirtualContactPhoneNumber(
    recipient,
    currentPhoneCountry,
  );

  // If no phone number, can't match (return false)
  if (!phoneNumber) {
    return false;
  }

  return recipients.some((existingRecipient) => {
    // Skip thread list suggestions when comparing by phone number
    // This case never happens as when we add threadListSuggestion,
    // we don't allow adding other recipients
    if (checkIfRecipientIsThreadListSuggestion(existingRecipient)) {
      return false;
    }
    const existingPhone = getVirtualContactPhoneNumber(
      existingRecipient,
      currentPhoneCountry,
    );
    return existingPhone && phoneNumber === existingPhone;
  });
};

const checkIfRecipientIsThreadListSuggestion = (
  recipient: Recipient,
): boolean => {
  return Boolean(
    typeof recipient === 'object' &&
      recipient !== null &&
      'threadListSuggestion' in recipient &&
      recipient.threadListSuggestion,
  );
};

// utils for the typeahead options

export const getFormattedBroadcastSuppressionDetails = (
  recipients: Recipient[],
  phoneInfos: TwilioPhoneType[],
): BroadcastSuppressionPhoneDetails[] => {
  if (checkIfRecipientIsThreadListSuggestion(recipients?.[0])) {
    return camel(phoneInfos);
  }
  return phoneInfos.map((phoneInfo) => {
    const matchedPhoneDetails: SuggestionType | string | undefined =
      recipients.find(
        (value) =>
          phoneInfo.phone_number === getVirtualContactPhoneNumber(value),
      );
    const fullName =
      matchedPhoneDetails && typeof matchedPhoneDetails !== 'string'
        ? getFullName(getVirtualContact(matchedPhoneDetails))
        : phoneInfo.phone_number || '-';
    return {
      phoneNumber: phoneInfo.phone_number,
      nationalFormat: phoneInfo.national_format,
      type: phoneInfo.type ?? 'unknown',
      broadcastSuppression: phoneInfo.broadcast_suppression ?? false,
      optedOut: Boolean(phoneInfo.opted_out),
      fullName,
      activePlacement: Boolean(phoneInfo.active_placement),
    };
  });
};

export const isThreadListSuggestion = (recipients: Recipient[]): Boolean => {
  return Boolean(
    recipients.length > 0 &&
      recipients[0] &&
      typeof recipients[0] === 'object' &&
      recipients[0] !== null &&
      'threadListSuggestion' in recipients[0] &&
      recipients[0].threadListSuggestion,
  );
};

const suggestionsById = (items: SuggestionType[]) =>
  new Map(
    items.flatMap((item) => {
      const id = getVirtualContact(item)?.id;
      return id ? [[id, item] as const] : [];
    }),
  );

export const resolveExactMatches = (
  exactMatchRefs: TypeaheadExactMatch[],
  contacts: SuggestionType[],
  audienceMembers: SuggestionType[],
): SuggestionType[] => {
  const contactById = suggestionsById(contacts);
  const audienceMemberById = suggestionsById(audienceMembers);

  return exactMatchRefs
    .map(({id, resultType}) =>
      (resultType === 'contact' ? contactById : audienceMemberById).get(id),
    )
    .filter((match): match is SuggestionType => Boolean(match));
};

export const getTransformedValues = (
  typeaheadResults: TypeaheadResults,
  values: Recipient[],
) => {
  const {
    audienceMembers = [],
    contacts = [],
    contactLists = [],
    exactMatches = [],
  } = typeaheadResults;

  const exactMatchIds = new Set(
    exactMatches
      .map((match) => getVirtualContact(match)?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const excludeExactMatches = (items: SuggestionType[]) =>
    items.filter((item) => {
      const id = getVirtualContact(item)?.id;
      return !id || !exactMatchIds.has(id);
    });

  const remainingAudienceMembers = excludeExactMatches(audienceMembers);
  const remainingContacts = excludeExactMatches(contacts);
  // this is needed to avoid contact/audience member + contact list combination
  const showContactLists = values.length === 0;

  return {
    People: [
      ...exactMatches,
      ...remainingAudienceMembers.slice(0, LIMIT),
      ...remainingContacts.slice(0, LIMIT),
    ],
    'Contact Lists': showContactLists ? contactLists.slice(0, LIMIT) : [],
    All: [
      ...remainingAudienceMembers.slice(LIMIT),
      ...remainingContacts.slice(LIMIT),
      ...(showContactLists ? contactLists.slice(LIMIT) : []),
    ],
  };
};

export const getTypeaheadOptions = ({
  grabbedPhoneNumbers,
  input,
  transformedTypeaheadResults,
  currentPhoneCountry,
  typeaheadResults,
  t,
}: {
  grabbedPhoneNumbers: {atsContact: AtsContact}[];
  input: string;
  transformedTypeaheadResults: {
    People: SuggestionType[];
    'Contact Lists': SuggestionType[];
    All: SuggestionType[];
  };
  currentPhoneCountry: string;
  typeaheadResults: TypeaheadResults;
  t: TranslationFunction;
}): Record<string, SuggestionType[]> => {
  // this case is when we are typing the phone number in the input
  if (
    input &&
    typeaheadResults.audienceMembers.length === 0 &&
    typeaheadResults.contacts.length === 0 &&
    typeaheadResults.contactLists.length === 0
  ) {
    const normalizedPhone = normalizePhone(input, currentPhoneCountry);
    return normalizedPhone
      ? {
          phoneNumber: [
            {
              atsContact: {
                id: normalizedPhone,
                phoneNumber: normalizedPhone,
                fullName: `${t(
                  'Send_message_to',
                  'Send message to',
                )} ${normalizedPhone}`,
                type: 'default',
              },
            },
          ],
        }
      : {};
  } else if (
    !input &&
    grabbedPhoneNumbers &&
    Array.isArray(grabbedPhoneNumbers) &&
    grabbedPhoneNumbers.length > 0
  ) {
    // this case is when we grab numbers in the "Grab numbers" functionality
    return {grabbedNumbers: grabbedPhoneNumbers};
  } else if (
    input &&
    (typeaheadResults.audienceMembers.length > 0 ||
      typeaheadResults.contacts.length > 0 ||
      typeaheadResults.contactLists.length > 0)
  ) {
    const normalizedPhone = normalizePhone(input, currentPhoneCountry);
    const allSuggestions = [
      ...transformedTypeaheadResults.People,
      ...transformedTypeaheadResults['Contact Lists'],
      ...transformedTypeaheadResults.All,
    ];
    const isNormalizedPhoneInResults = normalizedPhone
      ? allSuggestions.some(
          (suggestion) =>
            getVirtualContactPhoneNumber(suggestion, currentPhoneCountry) ===
            normalizedPhone,
        )
      : false;
    if (normalizedPhone && !isNormalizedPhoneInResults) {
      return {
        phoneNumber: [
          {
            atsContact: {
              id: normalizedPhone,
              phoneNumber: normalizedPhone,
              fullName: `${t(
                'Send_message_to',
                'Send message to',
              )} ${normalizedPhone}`,
              type: 'default',
            },
          },
        ],
        ...transformedTypeaheadResults,
      };
    }
    return transformedTypeaheadResults;
  } else {
    return {};
  }
};
